import {useEffect} from 'react';

import OLModifyInteraction from 'ol/interaction/Modify';
import OLSnapInteraction from 'ol/interaction/Snap';
import OLImageLayer from 'ol/layer/Image';
import OLVectorLayer from 'ol/layer/Vector';
import OLTileLayer from 'ol/layer/Tile';
import OLImageWMSSource from 'ol/source/ImageWMS';
import OLImageStaticSource from 'ol/source/ImageStatic';
import OLVectorSource from 'ol/source/Vector';
import OLTileWMSSource from 'ol/source/TileWMS';
import OLView from 'ol/View';

import {useObject} from 'topmeteo/general/hooks';

import {useMap} from './Map';

export {useMap};

const useMapAPI = (object, add, remove) => {
    return useMap(
        map => {
            map[add](object);

            if (remove) return () => map[remove](object);
        },
        [object, add, remove],
    );
};

const eventBinding = (object, name, handler) => {
    object.on(name, handler);

    return () => object.un(name, handler);
};

export const useEvent = (object, name, handler, dependencies) => {
    useEffect(
        () => {
            if (object) return eventBinding(object, name, handler);
        },
        dependencies ? [object, name, ...dependencies] : [object, name],
    );
};

export const useMapEvent = (name, handler, dependencies) => {
    return useMap(
        map => eventBinding(map, name, handler),
        dependencies ? [name, ...dependencies] : [name],
    );
};

export const useView = (options, dependencies) => {
    const view = useObject(() => new OLView(options), dependencies);

    useMapAPI(view, 'setView');

    return view;
};

export const useLayer = (factory, dependencies) => {
    const layer = useObject(factory, dependencies);

    useMapAPI(layer, 'addLayer', 'removeLayer');

    return layer;
};

export const useImageLayer = (options, dependencies) => (
    useLayer(() => new OLImageLayer(options), dependencies)
);

export const useImageWMSSource = (options, dependencies) => (
    useObject(() => new OLImageWMSSource(options), dependencies)
);

export const useImageStaticSource = (options, dependencies) => (
    useObject(() => new OLImageStaticSource(options), dependencies)
);

export const useVectorLayer = (options, dependencies) => (
    useLayer(() => new OLVectorLayer(options), dependencies)
);

export const useVectorSource = (options, dependencies) => (
    useObject(() => new OLVectorSource(options), dependencies)
);

export const useTileLayer = (options, dependencies) => (
    useLayer(() => new OLTileLayer(options), dependencies)
);

export const useTileWMSSource = (options, dependencies) => (
    useObject(() => new OLTileWMSSource(options), dependencies)
);

export const useInteraction = (factory, dependencies) => {
    const interaction = useObject(factory, dependencies);

    useMapAPI(interaction, 'addInteraction', 'removeInteraction');

    return interaction;
};

export const useSnapInteraction = (source, pixelTolerance) => (
    useInteraction(() => new OLSnapInteraction({source, pixelTolerance}), [source, pixelTolerance])
);

export const useModifyInteraction = source => (
    useInteraction(() => new OLModifyInteraction({source}), [source])
);
